import { ChangeDetectorRef, Component, ElementRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { StorageService, StorageKeys, BaseComponent, WindowService, ProductService, HelperService, ProductCategory } from "@mypxplat/xplat/core";
import { UserService } from "@mypxplat/xplat/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AppService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { MissingFenderSerialComponent, RegisterProductComponent } from "../modals";
import { ProductsListComponent } from "../products-list/products-list.component";
import { filter, take, takeUntil } from "rxjs";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-main-products",
  templateUrl: "main-products.component.html",
  standalone: false,
})
export class MainProductsComponent extends BaseComponent {
  public breadcrumbs: BreadcrumbItem[] = [];
  public loaded: boolean = false;
  public refreshing: boolean = false;
  public selectedProductCategory: ProductCategory = { string_id: "all" };
  public selectedCategory: any = "all";
  public selectedParentCategory: any;
  public loading: boolean = false;
  public hasProducts: boolean = false;
  public displayedProductCategories: ProductCategory[] = [];
  @ViewChild("contentContainer") contentContainer: ElementRef;
  @ViewChild("sharedProductsListCmp") sharedProductsListCmp: ProductsListComponent;

  constructor(
    userService: UserService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _win: WindowService,
    public productService: ProductService,
    public helperService: HelperService,
    public appService: AppService,
    public translate: TranslateService,
    public storageService: StorageService,
    private spinner: NgxSpinnerService,
    private _cdr: ChangeDetectorRef
  ) {
    super(userService);
  }

  ngOnInit() {
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "fui-header.my_gear" }];
    this.productService.products$
      .pipe(
        filter((item) => item),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.selectedProductCategory = this.productService.productCategories.filter((item) => item.string_id == this._activatedRoute.snapshot.params["category"])[0] || { string_id: "all" };
        this.updateDisplayedProductCategories();
      });

    this.appService.setPageTitle("Products");
    this.selectedCategory = this._activatedRoute.snapshot.params["category"];
    if (this.user && !this.productService.isGettingProducts && !this.productService.products?.software.length) {
      this.refresh();
    }

    if (this.user && this.productService.hardRefreshProducts) {
      setTimeout(() => {
        this.refresh();
        this.productService.hardRefreshProducts = false;
      }, 2000);
    }
  }

  private updateDisplayedProductCategories() {
    this.displayedProductCategories = this.productService.productCategories ? [...this.productService.productCategories] : [];
    this._cdr.detectChanges();
  }

  getRandomWidth(): string {
    const minWidth = 40; // Minimum percentage
    const maxWidth = 100; // Maximum percentage
    const randomWidth = Math.random() * (maxWidth - minWidth) + minWidth;
    return `${randomWidth.toFixed(2)}%`; // Convert to a percentage string
  }

  listLoaded(args) {
    this._win.setTimeout(() => {
      this.loaded = true;
      this.hasProducts = this.sharedProductsListCmp?.allProducts?.length > 0;
      if (this.contentContainer?.nativeElement) this.contentContainer.nativeElement.scrollTop = this.productService.lastProductListScrollPosition || 0;
      if (this.selectedCategory == "all" || this.selectedCategory == "other") {
        this.selectedParentCategory = this.selectedCategory;
      } else {
        if (this.productService.categoryMap[this.selectedCategory]) {
          this.selectedParentCategory = this.selectedCategory;
        }
      }
      this._cdr.detectChanges();
    }, 10);
  }

  /**
   * Hamburger click from the hero search cluster. Mirrors the community pattern:
   * desktop toggles the inline sidebar filter panel (`showFilterOptions`), mobile
   * opens the modal. `<fui-content-filter [showSidebarToggle]="false">` on the child
   * suppresses the built-in Show/Hide Filters text button, so this is the sole
   * refine affordance for the page.
   */
  toggleFilters() {
    if (!this.sharedProductsListCmp) return;
    const isMobile = typeof window !== "undefined" && window.innerWidth < 800;
    if (isMobile) {
      this.sharedProductsListCmp.fuiSidebarFilter?.showMobileModal();
    } else {
      this.sharedProductsListCmp.showFilterOptions = !this.sharedProductsListCmp.showFilterOptions;
    }
  }

  refresh() {
    this.refreshing = true;
    this.storageService.removeItem(StorageKeys.PRODUCTDETAILSMAP);
    this.storageService.removeItem(StorageKeys.PRODUCTIMAGESMAP);
    this.storageService.removeItem(StorageKeys.PRODUCTS);
    this.productService.getProducts(true, false, true, false).subscribe((result) => {
      this.refreshing = false;
    });
  }

  selectProductCategory(category) {
    this.selectedProductCategory = typeof category == "string" ? { string_id: category } : category;
    this._router.navigate(["products/", typeof category == "string" ? category : category.string_id], { replaceUrl: true });
  }

  contentContainerScroll(args) {
    this.productService.lastProductListScrollPosition = args.target.scrollTop;
  }

  go(prod_id) {
    this._router.navigate(["products/detail/", prod_id]);
  }

  registerProduct(event?) {
    const modalRef = this.appService.showModal(RegisterProductComponent, {
      size: "lg",
      ariaLabelledBy: "modal-title",
      beforeDismiss: () => {
        if (modalRef.componentInstance.registering) {
          alert(this.translate.instant("products.register.registration_in_process"));
          return false;
        }
      },
    });
  }
}
